import React, { useEffect, useState } from 'react';
import Loading from './Loading';
import { ReactComponent as Logo } from '../assets/logo.svg';

// Animation styles
const loadingScreenAnimations = `
  @keyframes logoEntrance {
    0% {
      opacity: 0;
      transform: translateY(-20px) scale(0.8);
    }
    40% {
      opacity: 1;
      transform: translateY(0) scale(1.05);
    }
    60% {
		opacity: 1;
      transform: translateY(0) scale(1.05);
    }
    100% {
		opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes logoExit {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-30px) scale(0.9);
    }
  }
  
  @keyframes loadingEntrance {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
  
  @keyframes bgGradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
  
  .star {
    position: absolute;
    background: rgb(248, 157, 189);
    border-radius: 50%;
    // opacity: 0.6;
    z-index: 0;
  }
`;

function LoadingScreen({ visible }: { visible: boolean }) {
	const [hide, setHide] = useState(false);
	const [animationState, setAnimationState] = useState('entering'); // 'entering', 'active', 'exiting'
	const MemoizedLogo = React.memo(Logo);

	// Generate stars for background
	const [stars] = useState(() =>
		Array.from({ length: 20 }, () => ({
			size: Math.random() * 4 + 2,
			top: `${Math.random() * 100}%`,
			left: `${Math.random() * 100}%`,
			animationDuration: `${Math.random() * 3 + 2}s`,
			animationDelay: `${Math.random() * 2}s`,
			opacity: Math.random() * 0.5 + 0.1,
		}))
	);

	useEffect(() => {
		// Initial entrance animation
		if (visible) {
			setHide(false);
			setAnimationState('entering');
			const timer = setTimeout(() => {
				setAnimationState('active');
			}, 800); // After entrance animation completes
			return () => clearTimeout(timer);
		}

		// Exit animation
		if (!visible && !hide) {
			setAnimationState('exiting');
			const timer = setTimeout(() => {
				setHide(true);
			}, 650); // Duration of exit animation + small buffer
			return () => clearTimeout(timer);
		}
	}, [visible, hide]);

	return (
		<>
			<style>{loadingScreenAnimations}</style>
			<div
				className={`absolute h-[100dvh] bg-white w-full z-10 flex items-center justify-center flex-col gap-[60px] pb-[260px] overflow-hidden ${hide ? 'hidden' : ''}`}
				style={{
					backgroundSize: '200% 200%',
					animation: visible ? 'bgGradientShift 8s ease infinite' : 'none',
					opacity: animationState === 'exiting' ? 0 : 1,
					visibility: animationState === 'exiting' ? 'hidden' : 'visible',
					transition: 'opacity 0.6s ease-in-out, visibility 0.6s ease-in-out',
				}}
			>
				{/* Decorative stars */}
				{stars.map((star, index) => (
					<div
						key={index}
						className="star"
						style={{
							width: `${star.size}px`,
							height: `${star.size}px`,
							top: star.top,
							left: star.left,
							opacity: star.opacity,
							animation: `pulse ${star.animationDuration} infinite ease-in-out`,
							animationDelay: star.animationDelay,
						}}
					/>
				))}

				{/* Logo with animations */}
				<div
					className="relative z-1"
					style={{
						animation: animationState === 'entering' ? 'logoEntrance 1s ease-out forwards' : animationState === 'exiting' ? 'logoExit 0.5s ease-in forwards' : 'pulse 3s infinite ease-in-out',
						opacity: animationState === 'entering' ? 0 : 1,
					}}
				>
					<MemoizedLogo
						width={300}
						height={200}
						style={{
							filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.2))',
						}}
					/>
				</div>

				{/* Loading spinner */}
				<div
					style={{
						animation: animationState === 'entering' ? 'loadingEntrance 0.8s ease-out 0.3s forwards' : animationState === 'exiting' ? 'fadeOut 0.4s ease-in forwards' : 'none',
						opacity: animationState === 'entering' ? 0 : 1,
					}}
				>
					<Loading />
				</div>
			</div>
		</>
	);
}

export default LoadingScreen;
